<template>
  <div class="home-view">
    <!-- 剧本列表 -->
    <div class="scripts-grid">
      <!-- 已有剧本卡片 -->
      <div v-for="item in scripts" :key="item.id" class="script-card">
        <div class="card-body" @click="openScript(item)">
          <div class="card-icon">
            <el-icon>
              <Document />
            </el-icon>
          </div>
          <div class="card-title" :title="item.title">{{ item.title }}</div>
          <div class="card-meta">更新于 {{ formatDate(item.update_at) }}</div>
        </div>
        <div class="card-actions">
          <el-button size="small" type="primary" @click.stop="openScript(item)">打开</el-button>
          <el-button size="small" @click.stop="renameScript(item)">重命名</el-button>
          <el-button size="small" type="danger" text @click.stop="removeScript(item.id)" class="delete-btn">
            <el-icon><Delete /></el-icon>
          </el-button>
        </div>
      </div>

      <!-- 新建剧本入口卡片（位于列表末尾） -->
      <div class="script-card create-card" role="button" tabindex="0"
           @click="dialogVisible = true"
           @keydown.enter="dialogVisible = true"
           @keydown.space.prevent="dialogVisible = true">
        <div class="create-top">
          <div class="create-circle">
            <span class="create-symbol">+</span>
          </div>
          <div class="create-title">新建作品</div>
        </div>
      </div>
    </div>

    <!-- 新建剧本弹窗（全面优化） -->
    <el-dialog 
      v-model="dialogVisible" 
      :title="null"
      width="640px" 
      :close-on-click-modal="false" 
      class="create-script-dialog"
      :show-close="false"
      :align-center="true"
      @close="resetForm"
    >
      <!-- 自定义标题区域 -->
      <div class="dialog-header">
        <div class="header-content">
          <div class="header-icon">
            <el-icon><Edit /></el-icon>
          </div>
          <div class="header-text">
            <h2 class="dialog-title">创建新剧本</h2>
            <p class="dialog-subtitle">开启你的创作之旅，打造精彩故事</p>
          </div>
        </div>
        <!-- <el-button class="close-btn" @click="dialogVisible = false" :icon="Close" circle></el-button> -->
      </div>

      <!-- 表单内容 -->
      <div class="dialog-body">
        <el-form 
          ref="formRef"
          :model="form" 
          :rules="formRules"
          label-position="top"
          class="create-form"
          @validate="onFieldValidate"
        >
          <div class="form-row">
            <el-form-item label="项目名称" prop="title" class="form-item-wide">
              <el-input
                v-model="form.title"
                placeholder="输入项目名称"
                maxlength="50"
                show-word-limit
                :validate-event="false"
              />
            </el-form-item>
          </div>

          <div class="form-row">
            <el-form-item label="剧本描述" prop="description" class="form-item-wide">
              <el-input 
                v-model="form.description" 
                type="textarea"
                placeholder="简要描述你的剧本内容、主题或故事背景..."
                :rows="3"
                maxlength="10000" 
                show-word-limit
                resize="none"
                 :validate-event="false"
                />
            </el-form-item>
          </div>

          <!-- 集数 + 类型 同行 -->
          <div class="form-row form-row-split">
            <el-form-item label="集数" prop="episodes" class="form-item-half">
              <el-input-number
                v-model="form.episodes"
                :min="1"
                :step="1"
                :max="999"
                controls-position="right"
                style="width: 100%;"
              />
            </el-form-item>

            <el-form-item label="核心受众" prop="channel" class="form-item-half">
              <el-select 
                v-model="form.channel" 
                placeholder="选择类型" 
                size="large"
                style="width: 100%"
                :prefix-icon="UserFilled"
              >
                <el-option label="🚹 男频" value="男频" />
                <el-option label="🚺 女频" value="女频" />
              </el-select>
            </el-form-item>
          </div>

          <div class="form-row">
            <el-form-item label="创作手法" prop="technique" class="form-item-wide">
              <el-select 
                v-model="form.technique" 
                placeholder="选择创作手法"
                style="width: 100%">
                <el-option label="情绪驱动 (IAP付费流)" value="情绪驱动 (IAP付费流)" />
                <el-option label="情节驱动（IAA广告流）" value="情节驱动（IAA广告流）" />
              </el-select>
            </el-form-item>
          </div>

          <div class="form-row">
            <el-form-item label="题材" prop="tags" class="form-item-wide">
              <div class="tags-box">
                <div class="dynamic-tags">
                  <el-tag
                    v-for="tag in presetThemeTags"
                    :key="tag"
                    :type="form.tags.includes(tag) ? 'primary' : 'info'"
                    :effect="form.tags.includes(tag) ? 'dark' : 'plain'"
                    class="selectable-tag"
                    @click="toggleThemeTag(tag)"
                  >
                    {{ tag }}
                  </el-tag>
                </div>
              </div>
            </el-form-item>
          </div>
        </el-form>
      </div>

      <!-- 自定义底部按钮 -->
      <div class="dialog-footer">
        <el-button 
          @click="dialogVisible = false" 
          size="large"
          class="cancel-btn"
        >
          取消创建
        </el-button>
        <el-button 
          type="primary" 
          @click="handleCreate"
          size="large" 
          class="submit-btn"
          :loading="isCreating"
          :disabled="!isFormValid"
        >
          <span v-if="!isCreating">开始创作</span>
          <span v-else>创建中...</span>
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { ref, getCurrentInstance, onMounted, computed, reactive } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage, ElMessageBox, ElLoading } from 'element-plus';
import { Document, Edit, Close, UserFilled, PriceTag, Plus, Delete } from '@element-plus/icons-vue';
import request from '@/api/request'

export default {
  name: 'HomeView',
  components: { Document, Edit, Close, UserFilled, Plus, Delete },
  setup() {
    const dialogVisible = ref(false);
    const isCreating = ref(false);
    const formRef = ref(null);
    
    const form = ref({ 
      title: '',
      description: '',
      channel: '', 
      episodes: 1,
      technique: '',
      tags: []
    });

    const scripts = ref([]);
    const validationState = reactive({
      description: null,
      channel: null
    });

    const { proxy } = getCurrentInstance();
    const router = useRouter();

    // 表单验证规则
    const formRules = {
      title: [
        { required: true, message: '请输入项目名称', trigger: 'blur' },
        { min: 1, max: 50, message: '名称长度为1-50个字符', trigger: 'blur' }
      ],
      description: [
        { required: true, message: '请输入剧本描述', trigger: 'blur' },
        { max: 10000, message: '描述不能超过10000个字符', trigger: 'blur' }
      ],
      channel: [
        { required: true, message: '请选择类型', trigger: 'change' }
      ],
      tags: [
        { type: 'array', required: true, message: '请至少选择1个题材', trigger: 'change' }
      ]
    };

    // 表单验证状态
    const isFormValid = computed(() => {
      return form.value.title.trim() &&
             form.value.description.trim() && 
             form.value.channel && 
             form.value.tags.length > 0;
    });

    // 字段验证回调
    const onFieldValidate = (prop, isValid, message) => {
      validationState[prop] = isValid ? 'success' : 'error';
    };

    // 重置表单
    const resetForm = () => {
      form.value = { 
        title: '',
        description: '',
        channel: '', 
        episodes: 1,
        technique: '',
        tags: []
      };
      Object.keys(validationState).forEach(key => {
        validationState[key] = null;
      });
      if (formRef.value) formRef.value.resetFields();
    };

    const presetThemeTags = [
      '战神/兵王', '赘婿', '神医', '穿越/重生', '霸总', '玄幻/仙侠', '宅斗/宫斗', '系统', '年代',
      '甜宠/团宠', '虐恋', '萌宝', '家庭伦理 (婆媳/凤凰男)', '悬疑/推理', '科幻/末世', '大女主'
    ];

    const toggleThemeTag = (tag) => {
      if (form.value.tags.includes(tag)) {
        form.value.tags = form.value.tags.filter(item => item !== tag);
      } else {
        form.value.tags.push(tag);
      }
      const p = formRef.value?.validateField('tags');
      if (p && typeof p.then === 'function') {
        p.catch(() => {});
      }
    };

    // 移除本地存储相关函数，每次都从接口获取最新数据

    // 从后端获取作品列表：每次都获取最新数据，不再使用本地存储
    const fetchScriptsFromApi = async () => {
      try {
        const res = await request.post('/playlet/search_for_script', {})
        let list = Array.isArray(res) ? res : (res?.data && Array.isArray(res.data) ? res.data : [])
        console.log(list);
        scripts.value = list
        // 不再保存到本地存储
      } catch (e) {
        // 出错时显示空列表
        scripts.value = [];
      }
    }

    const handleCreate = async () => {
      // 表单验证
      const valid = await formRef.value?.validate().catch(() => false);
      if (!valid) {
        ElMessage.warning('请完善表单信息');
        return;
      }

      isCreating.value = true;
      const loading = ElLoading.service({
        lock: true,
        text: '正在创建剧本，请稍候...',
        background: 'rgba(0, 0, 0, 0.35)'
      });

      try {
        // 组装参数
        const payload = {
          title: (form.value.title || '').trim(),
          number_of_episode: String(form.value.episodes ?? 1),
          core_audience: form.value.channel,
          writing_techniques: form.value.technique || '',
          script_description: (form.value.description || '').trim(),
          theme: (form.value.tags || []).join(';')
        };

        // 请求后端创建
        const data = await request.post('/playlet/create_a_script', payload);
        // 根据 request 拦截器，成功时 data 即为后端返回的 data 对象
        const newId = String(data?.id ?? Date.now());

          const newItem = {
            id: newId,
            title: String((data?.title ?? payload.title) || '新剧本'),
            description: String((data?.script_description ?? payload.script_description) || ''),
            channel: String((data?.core_audience ?? payload.core_audience) || ''),
            episodes: Number((data?.number_of_episode ?? payload.number_of_episode) || 1),
            technique: String((data?.writing_techniques ?? payload.writing_techniques) || ''),
            tags: String((data?.theme ?? payload.theme) || '')
              .split(';')
              .map(s => s.trim())
              .filter(s => s),
            createdAt: data?.created_at ? new Date(data.created_at).getTime() : Date.now(),
            updatedAt: data?.update_at ? new Date(data.update_at).getTime() : Date.now()
          };

        scripts.value.unshift(newItem);
        // 不再保存到本地存储

        dialogVisible.value = false;
        resetForm();

        ElMessage.success({
          message: '剧本创建成功！正在进入创作模式...',
          duration: 1500
        });

        // 传递 ID 到写作页面；同时备份存储一份当前剧本 ID
        localStorage.setItem('currentScriptId', newItem.id);
        // 将新创建的剧本信息存储到本地缓存
        localStorage.setItem('scriptInfo_' + newItem.id, JSON.stringify({
          title: newItem.title || '',
          core_audience: newItem.channel || '',
          writing_techniques: newItem.technique || '',
          theme: Array.isArray(newItem.tags) ? newItem.tags.join(', ') : (newItem.tags || ''),
          script_description: newItem.description || ''
        }));
        router.push({ 
          path: '/result', 
          query: { 
            id: newItem.id,
            action: 'create'
          } 
        });

      } catch (error) {
        // 错误提示已在 request 拦截器中处理，这里兜底提示
        ElMessage.error('创建失败，请稍后重试');
      } finally {
        loading.close();
        isCreating.value = false;
      }
    };

    const openScript = (item) => {
    console.log(item);
      localStorage.setItem('currentScriptId', item.id);
      // 将完整的剧本信息存储到本地缓存
      localStorage.setItem('scriptInfo', JSON.stringify(item));
      router.push({ 
        path: '/result', 
        query: { 
          id: String(item.id),
          action: 'open'
        } 
      });
    };

    const renameScript = async (item) => {
      try {
        const { value } = await ElMessageBox.prompt('请输入新的项目名称', '重命名', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          inputValue: item.title,
        });
        const name = (value || '').trim();
        if (!name) return;
        item.title = name;
        item.updatedAt = Date.now();
        // 不再保存到本地存储
        ElMessage.success('已重命名');
      } catch (e) { /* 用户取消 */ }
    };

    const removeScript = async (id) => {
      try {
        await ElMessageBox.confirm(
          '您即将删除这个剧本段落，删除后将无法恢复。\n\n确定要继续删除吗？', 
          '⚠️ 删除段落确认', 
          {
            type: 'warning',
            confirmButtonText: '确认删除',
            cancelButtonText: '取消',
            confirmButtonClass: 'el-button--danger',
            showCancelButton: true,
            closeOnClickModal: false,
            closeOnPressEscape: false,
            beforeClose: (action, instance, done) => {
              if (action === 'confirm') {
                instance.confirmButtonLoading = true;
                instance.confirmButtonText = '删除中...';
                setTimeout(() => {
                  done();
                }, 300);
              } else {
                done();
              }
            }
          }
        );
        
        // 显示加载状态
        const loadingInstance = ElLoading.service({
          lock: true,
          text: '删除中...',
          background: 'rgba(255, 255, 255, 0.6)'
        });
        
        try {
          // 调用删除接口
          const formData = new FormData();
          formData.append('script_id', id);
          
          const response = await request.post('/playlet/delete_the_script', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          
          // 删除成功后，用返回的数据更新页面
          if (response) {
            scripts.value = response;
          }
          
          ElMessage.success('已删除');
        } catch (error) {
          console.error('删除剧本失败:', error);
          ElMessage.error('删除失败，请重试');
        } finally {
          loadingInstance.close();
        }
      } catch (e) { /* 用户取消 */ }
    };

    const formatDate = (ts) => new Date(ts).toLocaleString();

    onMounted(async () => {
      // 每次页面加载都从接口获取最新数据
      const token = localStorage.getItem('token');
      let loadingInstance = null;
      if (token) {
        loadingInstance = ElLoading.service({
          lock: true,
          text: '加载中...',
          background: 'rgba(255, 255, 255, 0.6)'
        });
      }
      try {
        await fetchScriptsFromApi();
      } finally {
        if (loadingInstance) loadingInstance.close();
      }
    });

    return {
      dialogVisible, form, scripts, formRef, isCreating, formRules, isFormValid,
      handleCreate, openScript, removeScript, renameScript, resetForm, onFieldValidate,
      formatDate,
      UserFilled,
      presetThemeTags, toggleThemeTag
    };
  }
};
</script>

<style scoped>
.home-view {
  padding: 20px 24px;
  width: 100%;
  height: 100%;
}

.scripts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.script-card {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  transition: transform .15s ease, box-shadow .2s ease;
  cursor: default;
}

.script-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.08);
}

.card-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.card-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #f0f4ff;
  color: #667eea;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  margin-bottom: 12px;
}

.card-title {
  font-weight: 700;
  font-size: 18px;
  color: #222;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-meta {
  margin-top: 6px;
  font-size: 12px;
  color: #8592a3;
}

.card-actions {
  display: flex;
  gap: 10px;
  padding: 12px 16px 16px;
  justify-content: center;
}

/* 统一按钮样式 */
.card-actions .el-button {
  border-radius: 8px !important;
  font-weight: 500 !important;
  transition: all 0.3s ease !important;
  padding: 8px 16px !important;
  font-size: 13px !important;
  min-width: 60px !important;
  height: 32px !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06) !important;
}

/* 打开按钮（主要按钮） */
.card-actions .el-button--primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  border: none !important;
  color: #ffffff !important;
}

.card-actions .el-button--primary:hover {
  background: linear-gradient(135deg, #8797f0 0%, #764ba2 100%) !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4) !important;
}

/* 重命名按钮（次要按钮） */
.card-actions .el-button:not(.el-button--primary):not(.delete-btn) {
  background: #ffffff !important;
  border: 1px solid #e2e8f0 !important;
  color: #64748b !important;
}

.card-actions .el-button:not(.el-button--primary):not(.delete-btn):hover {
  background: #f1f5f9 !important;
  border-color: #cbd5e1 !important;
  color: #475569 !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.06) !important;
}

/* 删除按钮样式 */
.card-actions .delete-btn.el-button {
  background: #ffffff !important;
  border: 1px solid #ef4444 !important;
  color: #ef4444 !important;
  min-width: 32px !important;
  padding: 8px !important;
}

.card-actions .delete-btn.el-button:hover {
  background: #ef4444 !important;
  border-color: #ef4444 !important;
  color: #ffffff !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.4) !important;
}

.card-actions .delete-btn.el-button .el-icon {
  color: inherit !important;
  font-size: 14px !important;
}

/* 新建卡片 */
.create-card {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  cursor: pointer;
  outline: none;
}

.create-top {
  flex: 1;
  min-height: 200px;
  margin: 16px;
  border: 2px dashed var(--border-color, #cfd6e4);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, var(--background-secondary, #f7faff), var(--background-primary, #f9fbff));
  transition: background .25s ease, border-color .25s ease;
}

.create-circle {
  width: 88px;
  height: 88px;
  border-radius: 50%;
  border: 3px solid #98a6b5;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  color: #98a6b5;
  transition: transform .2s ease, background .25s ease, color .25s ease, border-color .25s ease;
}

.create-symbol {
  font-size: 40px;
  line-height: 1;
  font-weight: 700;
  margin-top: -6px;
}

.create-title {
  color: #606c80;
  font-size: 16px;
  font-weight: 600;
  transition: color .25s ease;
}

.create-card:hover .create-top {
  border-color: var(--primary-color, #667eea);
  background: linear-gradient(180deg, #f7faff, #eef4ff);
}

.create-card:hover .create-circle {
  border-color: var(--primary-color, #667eea);
  background: var(--primary-color, #667eea);
  color: #fff;
  transform: scale(1.06);
}

.create-card:hover .create-title {
  color: var(--primary-color, #667eea);
}

.create-card:focus-visible {
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.25);
}

/* 统一使用 ElementPlus 按钮作为交互元素的样式 */
.create-button {
  width: 60%;
  max-width: 260px;
  height: 44px;
  font-weight: 700;
}

 /* 新建剧本弹窗样式 */
 :deep(.create-script-dialog) {
   border-radius: 16px;
   overflow: hidden;
   background: linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%);
 }

:deep(.create-script-dialog .el-dialog__header) {
  display: none;
}

:deep(.create-script-dialog .el-dialog__body) {
  padding: 0;
  background: transparent;
}

:deep(.create-script-dialog .el-dialog__footer) {
  display: none;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 32px 32px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  position: relative;
  overflow: hidden;
}

.dialog-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="rgba(255,255,255,0.1)"/><circle cx="75" cy="75" r="1" fill="rgba(255,255,255,0.1)"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
  opacity: 0.3;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
  z-index: 1;
}

.header-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.header-text {
  flex: 1;
}

.dialog-title {
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 4px 0;
  color: white;
}

.dialog-subtitle {
  font-size: 13px;
  margin: 0;
  color: rgba(255, 255, 255, 0.85);
  font-weight: 400;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  position: relative;
  z-index: 1;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  color: white;
}

.dialog-body {
  padding: 32px;
  background: #fff;
}

.create-form {
  max-width: 100%;
}

.form-row {
  margin-bottom: 24px;
}

.form-row:last-child {
  margin-bottom: 0;
}

.form-row-split {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.form-item-primary :deep(.el-form-item__label) {
  font-weight: 600;
  color: #2d3748;
  font-size: 15px;
}

.form-item-wide :deep(.el-form-item__label),
.form-item-half :deep(.el-form-item__label) {
  font-weight: 600;
  color: #2d3748;
  font-size: 14px;
}

.tag-hint {
  font-size: 12px;
  color: #718096;
  margin-top: 6px;
  line-height: 1.4;
}

:deep(.el-input__wrapper) {
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;
}

:deep(.el-input__wrapper:hover) {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

:deep(.el-input__wrapper.is-focus) {
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
}

:deep(.el-textarea__inner) {
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;
}

:deep(.el-textarea__inner:hover) {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

:deep(.el-textarea__inner:focus) {
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
}

:deep(.el-select .el-input__wrapper) {
  border-radius: 8px;
}

.dialog-footer {
  padding: 24px 32px 32px;
  background: #f8faff;
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  border-top: 1px solid #e2e8f0;
}

.cancel-btn {
  background: #fff;
  border: 1px solid #d1d5db;
  color: #6b7280;
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 600;
  transition: all 0.2s ease;
}

.cancel-btn:hover {
  background: #f9fafb;
  border-color: #9ca3af;
  color: #4b5563;
  transform: translateY(-1px);
}

.submit-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 8px;
  padding: 12px 32px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  transition: all 0.2s ease;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
}

.submit-btn:disabled {
  background: #e5e7eb;
  color: #9ca3af;
  box-shadow: none;
  cursor: not-allowed;
}

/* 响应式优化：网格、卡片与标签在不同宽度下的适配 */
@media (max-width: 1200px) {
  .home-view { padding: 20px 16px; }
  .scripts-grid { grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 16px; }
  .card-body { padding: 16px; }
  .create-top { min-height: 180px; margin: 12px; }
}

@media (max-width: 992px) {
  .scripts-grid { grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 14px; }
  .card-actions { flex-wrap: wrap; gap: 6px; }
  .create-circle { width: 72px; height: 72px; }
  .create-symbol { font-size: 34px; }
}

@media (max-width: 768px) {
  .home-view { padding: 16px 12px; }
  .scripts-grid { grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 12px; }
  .card-title { font-size: 16px; }
  .card-meta { font-size: 11px; }
  .create-top { min-height: 150px; margin: 10px; }
  .tags-box { padding: 8px 10px; }
  .tag-input { width: 120px; }
}

@media (max-width: 480px) {
  .scripts-grid { grid-template-columns: 1fr; gap: 10px; }
  .card-body { padding: 12px; }
  .create-circle { width: 64px; height: 64px; }
  .create-symbol { font-size: 28px; }
  .card-actions { gap: 4px; }
  .submit-btn, .cancel-btn { width: 100%; }
}
  
  .header-content {
    gap: 12px;
  }
  
  .header-icon {
    width: 48px;
    height: 48px;
    font-size: 20px;
  }
  
  .dialog-title {
    font-size: 18px;
  }
  
  .dialog-subtitle {
    font-size: 13px;
  }

/* 新增：标签容器与间距样式 */
.tags-box {
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 8px;
  padding: 5px 12px;
  min-width: 100%;
}

.dynamic-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  min-height: 36px;
}

.selectable-tag {
  cursor: pointer;
  user-select: none;
}

.selectable-tag:hover {
  opacity: 0.9;
}
</style>
